/* eslint-disable @typescript-eslint/no-explicit-any */
// import { SQLiteQuery } from './query';
// import { SQLiteQueue } from './queue';
// import { DatabaseJSON } from './types';
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite'
import { Capacitor } from '@capacitor/core'
import { generateTableSQLFromObject, SQLiteColumnType } from './schema'
import { cloneDeep } from 'lodash'
import { ShapeTemplateType } from '../../app/data/type'
import { InspectionType, VoiceFile } from '../../app/data/types'

// SQL 쿼리 상수 정의
export const APP_TABLE_NAMES = [
  'defects',
  'facility_photos',
  'defect_photos',
  'device_event_log',
  'docs',
  'tasks',
  'floorplans',
  'voice_files',
  // 품질점검(QUALITY) 전용 테이블 - QUALITY 과업 진입 시에만 데이터가 적재됨
  'quality_checklist_zones',
  'quality_checklist_items',
  'quality_inspections',
  'quality_inspection_results',
  'quality_result_photos',
  'quality_partners',
] as const

export type AppTableName = (typeof APP_TABLE_NAMES)[number]

// /**
//  * SQLite 데이터베이스를 관리하는 클래스
//  * @extends SQLiteQueue - 쿼리를 순차적으로 제공하는 큐 시스템
//  */
// export class SQLiteDatabase extends SQLiteQueue {
//   /**
//    * 데이터베이스를 초기화하고 인스턴스를 생성합니다
//    * @param {Record<string, TableSchema>} tableSchemas - 테이블 스키마 정의
//    * @param {DatabaseJSON} [data] - 초기 데이터베이스 데이터
//    * @returns {Promise<SQLiteDatabase>} 초기화된 데이터베이스 인스턴스
//    */
//   public static async create(
//     tableSchemas: Record<
//       string,
//       {
//         schema: string;
//         name: string;
//       }
//     >,
//     data?: DatabaseJSON,
//   ): Promise<SQLiteDatabase> {
//     const instance = new SQLiteDatabase({ tables: tableSchemas, data });
//     await instance.initPromise;
//     return instance;
//   }
// }

//-------------------------------------------------------
// SQLite 데이터베이스 연결 관리
// 로컬에 db파일 생성용
class SQLiteManager {
  private static instance: SQLiteManager | null = null
  private sqliteConnection: SQLiteConnection
  private db: any = null
  //현재 버전 2.0.0 데이터 구조 변경으로 테이블 새로생성
  private dbName = 'local_database_v2_.db'
  private isConnecting = false
  public createdTables: Set<string> = new Set()

  // 연결 유효성(쓰기 가능) 검사 최소 간격.
  // _write_test의 CREATE/DROP은 fsync·배타 락을 동반한 DDL 쓰기 트랜잭션이므로
  // 매 호출 검사하면 모든 DB 작업이 네이티브 큐에서 직렬로 지연된다.
  // 검사는 호출 경로(사용자 저장 등) 밖에서, DB 호출이 이 시간 동안 없을 때 돌린다.
  private static readonly VALIDATE_INTERVAL_MS = 30_000
  private static readonly VALIDATE_IDLE_MS = 1_000
  private lastValidatedAt = 0
  private lastAccessAt = 0
  private validateTimer: ReturnType<typeof setTimeout> | null = null
  private isValidating = false
  // 앱 복귀 직후(iOS 백그라운드 뒤 읽기 전용 전환 대비) 첫 호출은 지금처럼 즉시 검사한다
  private validateOnNextAccess = false

  private constructor() {
    this.sqliteConnection = new SQLiteConnection(CapacitorSQLite)
  }

  public static getInstance(): SQLiteManager {
    if (!SQLiteManager.instance) {
      SQLiteManager.instance = new SQLiteManager()
    }
    return SQLiteManager.instance
  }

  public async getDatabase(): Promise<any> {
    // 연결 중이면 대기
    if (this.isConnecting) {
      let attempts = 0
      while (this.isConnecting && attempts < 30) {
        await new Promise(resolve => setTimeout(resolve, 100))
        attempts++
      }
      if (this.isConnecting) {
        throw new Error('Database connection timeout')
      }
    }

    // 기존 연결: 즉시 반환하고 주기 검사는 유휴 타이머로 미룬다 (매 호출 DDL 검사 금지).
    // 30초 검사가 사용자 저장에 끼어들면 브리지 왕복 2회(SELECT 1 + DDL 쓰기 트랜잭션)가 저장을 막는다.
    if (this.db) {
      this.lastAccessAt = Date.now()
      if (this.validateOnNextAccess) {
        this.validateOnNextAccess = false
        if (await this.validateConnection()) return this.db
      } else {
        if (Date.now() - this.lastValidatedAt >= SQLiteManager.VALIDATE_INTERVAL_MS) {
          this.scheduleIdleValidation()
        }
        return this.db
      }
    }

    return this.createConnection()
  }

  /**
   * 앱 복귀 직후 첫 DB 호출에서 연결을 즉시 검사하도록 표시한다 (백그라운드 동안 읽기 전용이 된 연결 대비)
   */
  public markNeedsValidation(): void {
    this.validateOnNextAccess = true
  }

  // DB 호출이 VALIDATE_IDLE_MS 동안 없을 때 검사 (그 사이 호출이 있으면 다시 미룸)
  private scheduleIdleValidation(): void {
    if (this.validateTimer || this.isValidating) return
    this.validateTimer = setTimeout(() => {
      this.validateTimer = null
      if (!this.db || this.isConnecting) return
      if (Date.now() - this.lastValidatedAt < SQLiteManager.VALIDATE_INTERVAL_MS) return
      if (Date.now() - this.lastAccessAt < SQLiteManager.VALIDATE_IDLE_MS) {
        this.scheduleIdleValidation()
        return
      }
      void this.validateConnection()
    }, SQLiteManager.VALIDATE_IDLE_MS)
  }

  /**
   * 연결이 읽기·쓰기 가능한지 검사한다. 실패하면 연결을 닫아 다음 getDatabase가 새로 만들게 한다.
   * @returns 유효하면 true
   */
  private async validateConnection(): Promise<boolean> {
    const db = this.db
    if (!db) return false
    this.isValidating = true
    try {
      await db.query('SELECT 1')
      // 쓰기 가능한지 테스트 (읽기 전용 상태 감지)
      await db.execute(
        'CREATE TABLE IF NOT EXISTS _write_test (id INTEGER PRIMARY KEY); DROP TABLE IF EXISTS _write_test;',
      )
      this.lastValidatedAt = Date.now()
      return true
    } catch (e: any) {
      const errorMsg = e?.message?.toLowerCase() || ''
      // 읽기 전용 에러 감지 (code 1032 또는 readonly 메시지)
      if (errorMsg.includes('readonly') || errorMsg.includes('1032') || e?.code === 1032) {
        console.warn('[SQLITE] Database is in readonly mode, forcing reconnection...')
      } else {
        console.info('Existing connection invalid, creating new one')
      }
      // 검사 중 다른 경로가 이미 연결을 바꿨으면 새 연결은 건드리지 않는다
      if (this.db !== db) return false
      // 강제로 연결 닫기 시도
      try {
        await this.sqliteConnection.closeConnection(this.dbName, false)
      } catch {
        // 닫기 에러는 무시
      }
      if (this.db === db) this.db = null
      return false
    } finally {
      this.isValidating = false
    }
  }

  private async createConnection(): Promise<any> {
    this.isConnecting = true

    try {
      const platform = Capacitor.getPlatform()

      if (platform === 'web') {
        await this.sqliteConnection.initWebStore()
      }

      // 기존 연결 확인 및 재사용
      try {
        const existingConnections = await this.sqliteConnection.retrieveAllConnections()
        for (const [dbName, connection] of existingConnections.entries()) {
          if (dbName === this.dbName) {
            console.info('[SQLITE] 기존 연결 존재, 재사용')
            this.db = connection
            await this.db.query('SELECT 1')
            await this.setupDatabase()
            return this.db
          }
        }
      } catch (error) {
        console.warn('[SQLITE] 기존 연결 존재 확인 중 오류 발생:', error)
      }

      // 새 연결 생성
      try {
        const connection = await this.sqliteConnection.createConnection(
          this.dbName,
          false,
          'no-encryption',
          1,
          false,
        )
        await connection.open()
        await connection.query('SELECT 1')

        this.db = connection
        await this.setupDatabase()

        console.info('[SQLITE] 데이터베이스 연결 성공')
        return this.db
      } catch (createError: any) {
        // 1. "이미 존재한다"는 에러가 발생한 경우
        if (createError.message?.includes('already exists')) {
          console.info('[SQLITE] 연결 존재, 재사용 시도')

          try {
            // 2. 연결 회수 시도
            const connection = await this.sqliteConnection.retrieveConnection(this.dbName, false)
            await connection.query('SELECT 1') // 유효성 검사

            this.db = connection
            await this.setupDatabase()
            console.info('[SQLITE] 데이터베이스 재사용 성공')
            return this.db
          } catch (retrieveError) {
            // 3. (좀비 연결 상태) -> 강제 종료 후 재생성
            console.warn(
              'Retrieve failed, connection might be in zombie state. Force closing...',
              retrieveError,
            )

            try {
              // 강제로 닫기 시도 (에러 무시)
              await this.sqliteConnection.closeConnection(this.dbName, false)
            } catch (_closeError) {
              // 닫기 에러는 무시하고 진행
            }

            // 4. 다시 생성 시도 (재귀 호출 대신 직접 수행하여 무한루프 방지)
            const newConnection = await this.sqliteConnection.createConnection(
              this.dbName,
              false,
              'no-encryption',
              1,
              false,
            )
            await newConnection.open()
            this.db = newConnection
            await this.setupDatabase()
            console.info('[SQLITE] 데이터베이스 강제 닫기 후 재생성 성공')
            return this.db
          }
        } else {
          // "already exists"가 아닌 다른 에러는 그대로 던짐
          throw createError
        }
      }
    } catch (error) {
      console.error('[SQLITE] 데이터베이스 연결 생성 실패:', error)
      this.db = null
      throw error
    } finally {
      this.isConnecting = false
    }
  }

  private async setupDatabase(): Promise<void> {
    await this.optimizeDatabase()
    await this.loadExistingTables()
    await this.initializeTables()
    await this.ensureColumns()
    await this.ensureIndexes()
    await this.migrateFloatIds()
    await this.purgeDisabledDeviceEventLog()
  }

  // device_event_log 기능 전역 비활성화(2026-06-10) 이전에 단말에 누적된 레거시 로그를
  // 1회성으로 비우고 VACUUM으로 디스크를 회수한다.
  // - 비활성화 이후 신규 생성/sync/cleanup이 전부 차단되어, 기존 row는 어떤 경로로도
  //   삭제되지 않고 DB 파일을 영구히 비대하게 유지하는 문제(재시작에도 지속)를 해소.
  // - localStorage 버전 플래그로 단 1회만 실행(VACUUM은 비용이 크므로 매 부팅 반복 금지).
  // - 기능을 다시 켜는 경우, 플래그 버전을 올려 재실행 여부를 별도 판단할 것.
  private async purgeDisabledDeviceEventLog(): Promise<void> {
    if (!this.db) return
    const PURGE_FLAG = 'DEVICE_EVENT_LOG_PURGED_V1'
    try {
      if (localStorage.getItem(PURGE_FLAG)) return
      if (!this.createdTables.has('device_event_log')) {
        // 테이블이 없으면(신규 설치 등) 정리할 것이 없으므로 플래그만 세팅
        localStorage.setItem(PURGE_FLAG, 'done')
        return
      }
      const before = await this.db.query('SELECT COUNT(*) AS cnt FROM device_event_log')
      const beforeCount = Number(before.values?.[0]?.cnt ?? 0)
      if (beforeCount === 0) {
        // 비울 데이터가 없으면 불필요한 VACUUM(부팅 지연) 회피
        localStorage.setItem(PURGE_FLAG, 'done')
        return
      }
      // 데이터만 비우고 스키마는 보존
      await this.db.run('DELETE FROM device_event_log')
      // VACUUM 성패와 무관하게 1회만 실행하도록 DELETE 직후 플래그 세팅
      localStorage.setItem(PURGE_FLAG, 'done')
      try {
        // 빈 공간 회수(best-effort). VACUUM은 트랜잭션 내 실행 불가 → transaction=false
        await this.db.execute('VACUUM', false)
        console.info(`[SQLITE] device_event_log 레거시 로그 ${beforeCount}건 정리 + VACUUM 완료`)
      } catch (vacuumError) {
        console.warn(
          `[SQLITE] device_event_log ${beforeCount}건 정리 완료, VACUUM 실패:`,
          vacuumError,
        )
      }
    } catch (e) {
      // 플래그 미설정 상태이므로 다음 부팅에서 재시도됨
      console.warn('[SQLITE] purgeDisabledDeviceEventLog 실패:', e)
    }
  }

  /**
   * iOS Capacitor SQLite native bridge가 number를 TEXT 컬럼에 stringify할 때
   * "15522" 같은 정수값을 "15522.0"으로 저장하던 문제(prepareSQLiteValue 수정 전)로
   * 인해 PRIMARY KEY인 id 컬럼에 ".0" 형태로 박힌 row가 존재하면, 동일 의미의 정수
   * id로 들어오는 INSERT OR REPLACE 가 별개 row로 인식되어 데이터가 2배로 보인다.
   *
   * 모든 테이블의 id LIKE '%.0' row를 정규화한다.
   * 정규화 결과와 같은 id가 이미 존재하면(=정상 row + ".0" row 공존) ".0" row를 삭제.
   * 그 외 ".0" row는 그대로 normalize 한다.
   *
   * 매 setupDatabase 호출마다 실행한다(idempotent: ".0" row가 없으면 0 row 영향).
   * 어떤 경로로든 ".0" row가 다시 생긴 경우 다음 부팅에서 자동 청소된다.
   */
  private async migrateFloatIds(): Promise<void> {
    if (!this.db) return
    // iOS Capacitor SQLite native bridge가 number를 TEXT 컬럼에 "557.0" 형태로
    // 저장하던 문제로 인한 잔존 데이터를 정리한다. Android/Web 은 이 문제가 없으므로
    // 매번 실행해도 0 row 영향이지만 startup 비용을 아끼기 위해 iOS에서만 실행한다.
    if (Capacitor.getPlatform() !== 'ios') return

    // setupDatabase 시점에 동적 생성 테이블(tasks 등)은 createdTables에 없을 수 있어
    // sqlite_master를 직접 query 한다.
    let tableNames: string[] = []
    try {
      const result = await this.db.query(
        // eslint-disable-next-line quotes
        `SELECT name FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%' AND name != '_write_test'`,
      )
      tableNames = (result.values ?? [])
        .map((r: { name?: string }) => r?.name ?? '')
        .filter((n: string) => n.length > 0)
    } catch (e) {
      console.warn('[SQLITE] migrateFloatIds: enumerate tables failed', e)
      return
    }

    let totalDup = 0
    let totalNorm = 0
    for (const tableName of tableNames) {
      try {
        // id 컬럼이 없는 테이블은 skip
        const cols = await this.db.query(`PRAGMA table_info(${tableName})`)
        const hasId = (cols.values ?? []).some((c: { name?: string }) => c?.name === 'id')
        if (!hasId) continue

        // 1) ".0" 정규화 결과가 이미 존재하는 row → ".0" 쪽 삭제 (PRIMARY KEY 충돌 방지)
        const dupRes = await this.db.run(
          `DELETE FROM ${tableName}
           WHERE id LIKE '%.0'
             AND SUBSTR(id, 1, LENGTH(id) - 2) IN (
               SELECT id FROM ${tableName} WHERE id NOT LIKE '%.0'
             )`,
        )
        const dupChanges = dupRes?.changes?.changes ?? 0

        // 2) 남은 ".0" row 정규화
        const updRes = await this.db.run(
          `UPDATE ${tableName} SET id = SUBSTR(id, 1, LENGTH(id) - 2) WHERE id LIKE '%.0'`,
        )
        const updChanges = updRes?.changes?.changes ?? 0

        totalDup += dupChanges
        totalNorm += updChanges
      } catch (e) {
        console.warn(`[SQLITE] migrateFloatIds ${tableName} FAIL`, e)
      }
    }

    // 정상 케이스에는 noisy하지 않도록 변경이 있을 때만 한 줄 로그
    if (totalDup || totalNorm) {
      console.info(`[SQLITE] migrateFloatIds - dup_deleted=${totalDup}, normalized=${totalNorm}`)
    }
  }

  /**
   * 기존 테이블에 나중에 추가된 컬럼을 앱 시작 시 보장한다.
   * 스토어 initData의 컬럼 추가(checkTableExists)는 온라인일 때만 실행되는 경로가 있어,
   * 오프라인 상태로 업그레이드하면 컬럼이 없어 updateSqliteOnly가 새 필드를 조용히 버린다.
   * 네트워크와 무관하게 setupDatabase에서 실행한다(idempotent: 있으면 건너뜀).
   */
  private async ensureColumns(): Promise<void> {
    if (!this.db) return

    const columnDefs: { table: string; column: string; type: SQLiteColumnType }[] = [
      // 사진 그리기 (벡터 JSON 문자열)
      { table: 'defect_photos', column: 'drawing', type: 'TEXT' },
      { table: 'facility_photos', column: 'drawing', type: 'TEXT' },
      // 보고서 사용 사진 (기존엔 온라인 병합 시에만 추가됨)
      { table: 'defect_photos', column: 'is_main', type: 'INTEGER' },
    ]

    const columnsByTable = new Map<string, Set<string>>()
    for (const { table, column, type } of columnDefs) {
      if (!this.createdTables.has(table)) continue
      try {
        let existing = columnsByTable.get(table)
        if (!existing) {
          const result = await this.db.query(`PRAGMA table_info(${table})`)
          existing = new Set((result.values ?? []).map((col: any) => String(col.name)))
          columnsByTable.set(table, existing)
        }
        if (existing.has(column)) continue
        await this.db.execute(`ALTER TABLE ${table} ADD COLUMN ${column} ${type}`)
        existing.add(column)
        console.info(`[SQLITE] 컬럼 추가: ${table}.${column} (${type})`)
      } catch (error) {
        console.error(`[SQLITE] 컬럼 추가 실패 (${table}.${column}):`, error)
      }
    }
  }

  private async ensureIndexes(): Promise<void> {
    if (!this.db) return

    // 서버 스키마 + 앱 쿼리 패턴 기반 인덱스
    const indexDefs: { table: string; column: string }[] = [
      // task_id - initData, eviction, sync 전체에서 사용
      { table: 'defects', column: 'task_id' },
      { table: 'defect_photos', column: 'task_id' },
      { table: 'facility_photos', column: 'task_id' },
      { table: 'floorplans', column: 'task_id' },
      { table: 'docs', column: 'task_id' },
      { table: 'voice_files', column: 'task_id' },
      { table: 'device_event_log', column: 'task_id' },
      // localStringId - sync 매핑 쿼리에서 사용
      { table: 'defects', column: 'localStringId' },
      { table: 'defect_photos', column: 'localStringId' },
      { table: 'facility_photos', column: 'localStringId' },
      { table: 'floorplans', column: 'localStringId' },
      { table: 'docs', column: 'localStringId' },
      { table: 'voice_files', column: 'localStringId' },
      // facility_id - 시설물 단위 조회
      { table: 'defects', column: 'facility_id' },
      { table: 'facility_photos', column: 'facility_id' },
      { table: 'floorplans', column: 'facility_id' },
      // floorplan_id - 도면 단위 결함/사진 조회
      { table: 'defects', column: 'floorplan_id' },
      { table: 'defect_photos', column: 'floorplan_id' },
      // defect_id - 결함별 사진 조회
      { table: 'defect_photos', column: 'defect_id' },
      // 품질점검 - task 단위 정리/조회
      { table: 'quality_checklist_zones', column: 'task_id' },
      { table: 'quality_checklist_items', column: 'task_id' },
      { table: 'quality_inspections', column: 'task_id' },
      { table: 'quality_inspection_results', column: 'task_id' },
      { table: 'quality_result_photos', column: 'task_id' },
      { table: 'quality_partners', column: 'task_id' },
      // 품질점검 - sync 매핑 쿼리
      { table: 'quality_inspections', column: 'localStringId' },
      { table: 'quality_inspection_results', column: 'localStringId' },
      { table: 'quality_result_photos', column: 'localStringId' },
      // 품질점검 - 구역/세션/결과 단위 조회
      { table: 'quality_checklist_items', column: 'zone_id' },
      { table: 'quality_inspection_results', column: 'inspection_id' },
      { table: 'quality_result_photos', column: 'inspection_id' },
      { table: 'quality_result_photos', column: 'result_id' },
    ]

    for (const { table, column } of indexDefs) {
      if (!this.createdTables.has(table)) continue
      try {
        await this.db.execute(
          `CREATE INDEX IF NOT EXISTS idx_${table}_${column} ON ${table}(${column})`,
        )
      } catch {
        // 인덱스 생성 실패는 무시 (기능에 영향 없음, 성능만 저하)
      }
    }
  }

  /**
   * 앱에서 사용하는 모든 테이블을 초기화합니다.
   * 이미 존재하는 테이블은 건너뜁니다.
   */
  private async initializeTables(): Promise<void> {
    if (!this.db) return

    const tableSchemas = this.getDefaultTableSchemas()

    for (const tableName of APP_TABLE_NAMES) {
      // 이미 존재하는 테이블은 건너뜀
      if (this.createdTables.has(tableName)) {
        continue
      }

      try {
        const defaultData = tableSchemas[tableName]
        if (!defaultData || defaultData.length === 0) {
          continue
        }

        const createTableSQL = await this.getDynamicTableSql(tableName, defaultData)
        if (createTableSQL && createTableSQL.trim()) {
          await this.db.execute(createTableSQL)
          this.createdTables.add(tableName)
          console.info(`[SQLITE] 테이블 생성 완료: ${tableName}`)
        }
      } catch (error) {
        console.error(`[SQLITE] 테이블 생성 실패 (${tableName}):`, error)
      }
    }
  }

  /**
   * 동적으로 테이블 생성 SQL을 생성합니다.
   * @param {string} tableName - 테이블 이름
   * @param {any} data - 테이블 구조를 정의할 데이터
   * @param {boolean} isAlone - task_id 컬럼 제외 여부
   * @returns {Promise<string | undefined>}
   */
  private async getDynamicTableSql(
    tableName: string,
    data: any,
    isAlone: boolean = false,
  ): Promise<string | undefined> {
    let createTableSQL
    const options = {
      idField: 'id',
      idType: 'TEXT' as SQLiteColumnType,
      includeDeletedField: false,
    }
    try {
      if (Array.isArray(data)) {
        if (data.length > 0) {
          const tempData = cloneDeep(data[0])
          if (!isAlone) {
            tempData.task_id = 0
          }
          createTableSQL = generateTableSQLFromObject(tableName, tempData, options)
        }
      } else {
        const tempData = cloneDeep(data)
        if (!isAlone) {
          tempData.task_id = 0
        }
        createTableSQL = generateTableSQLFromObject(tableName, tempData, options)
      }
      return createTableSQL
    } catch (error) {
      console.error(`Error creating dynamic table ${tableName}:`, error)
      return undefined
    }
  }

  /**
   * 각 테이블의 기본 스키마 데이터를 반환합니다.
   * @returns {Record<AppTableName, any[]>}
   */
  private getDefaultTableSchemas(): Record<AppTableName, any[]> {
    return {
      defects: [
        {
          id: 0,
          task_id: 0,
          facility_id: 0,
          floorplan_id: 0,
          company_id: 0,
          created_user_id: 0,
          previous_defect_id: 0,
          parent_defect_id: 0,
          shape_type: '',
          shape_data: {},
          color: '',
          memo: '',
          structural_component: '',
          classification: '',
          progressing: 0,
          width: '',
          length: '',
          area: '',
          count: '',
          is_structural: 0,
          repair_plan: '',
          cause_estimate: '',
          template: 'custom-RC' as ShapeTemplateType,
          previous_balloon_number: '',
          sort_key: '',
          fields_updated_at: {},
          created_at: '',
          updated_at: '',
          deleted_at: '',
          localStringId: '',
          modifiedFromLocal: false,
          hasPhoto: false,
          mainPhotoUrl: '',
          prevPhotoUrl: '',
          indexing: '',
          defect_photos: [],
          inspection_type: 'STATUS_OUTSIDE' as InspectionType,
          test_measure_data: null,
        },
      ],
      facility_photos: [
        {
          id: 0,
          facility_id: 0,
          task_id: 0,
          company_id: 0,
          created_user_id: 0,
          memo: '',
          url: '',
          drawing: null,
          fields_updated_at: {},
          created_at: '',
          updated_at: '',
          deleted_at: '',
          localStringId: '',
          sort_key: '',
          modifiedFromLocal: false,
        },
      ],
      defect_photos: [
        {
          id: 0,
          defect_id: 0,
          task_id: 0,
          company_id: 0,
          facility_id: 0,
          floorplan_id: 0,
          created_user_id: 0,
          url: '',
          is_previous: false,
          is_main: false,
          drawing: null,
          sort_key: '',
          fields_updated_at: {},
          created_at: '',
          updated_at: '',
          deleted_at: '',
          localShapeId: '',
          localStringId: '',
          modifiedFromLocal: false,
          inspection_type: 'STATUS_OUTSIDE' as InspectionType,
        },
      ],
      device_event_log: [
        {
          id: 0,
          task_id: 0,
          related_id: 0,
          event_type: '',
          environment: '',
          company_id: 0,
          created_user_id: 0,
          author: '',
          old_value: '',
          new_value: '',
          fields_updated_at: {},
          created_at: '',
          updated_at: '',
          deleted_at: '',
          deleted: 0,
          localRelatedId: '',
          modifiedFromLocal: false,
        },
      ],
      docs: [
        {
          id: 0,
          template_id: 0,
          company_id: 0,
          task_id: 0,
          facility_id: 0,
          page_no: 0,
          items: '[]',
          fields_updated_at: {},
          created_at: '',
          updated_at: '',
          deleted_at: '',
          brush_data: [],
          localStringId: '',
          modifiedFromLocal: false,
          url: '',
        },
      ],
      tasks: [],
      floorplans: [
        {
          id: 0,
          facility_id: 0,
          task_id: 0,
          company_id: 0,
          created_user_id: 0,
          name: '',
          sort_key: '',
          file_url: '',
          origin_file_url: '',
          rotate: 0,
          tile_config: {
            bounds: { width: 0, height: 0 },
            levels: [],
            tileSize: 0,
            floorplanId: 0,
            createdAt: 0,
          },
          tile_base_url: '',
          is_tiled: false,
          width: 0,
          height: 0,
          balloon_scale: 0,
          balloon_font_scale: 0,
          connected_line_thickness_scale: 0,
          point_shape_scale: 0,
          area_shape_border_thickness_scale: 0,
          fields_updated_at: {},
          created_at: '',
          updated_at: '',
          deleted_at: '',
          localStringId: '',
          modifiedFromLocal: false,
        },
      ],
      voice_files: [
        {
          id: 0,
          task_id: 0,
          facility_id: 0,
          floorplan_id: 0,
          defect_id: 0,
          template_id: 0,
          record_item_key: '',
          company_id: 0,
          created_user_id: 0,
          url: '',
          name: '',
          stt_text: '',
          summary_text: '',
          duration_ms: 0,
          fields_updated_at: {},
          created_at: '',
          updated_at: '',
          deleted_at: '',
          localStringId: '',
          modifiedFromLocal: false,
          fileName: '',
        } as VoiceFile,
      ],
      quality_checklist_zones: [
        {
          id: 0,
          company_id: 0,
          set_id: 0,
          task_id: 0,
          source_zone_id: 0,
          created_user_id: 0,
          name: '',
          location_type: '',
          locations: [],
          sort_key: '',
          fields_updated_at: {},
          created_at: '',
          updated_at: '',
          deleted_at: '',
          localStringId: '',
          modifiedFromLocal: false,
        },
      ],
      quality_checklist_items: [
        {
          id: 0,
          company_id: 0,
          set_id: 0,
          zone_id: 0,
          task_id: 0,
          source_item_id: 0,
          created_user_id: 0,
          trade: '',
          item_no: '',
          question: '',
          defect_content: '',
          locations: [],
          sort_key: '',
          fields_updated_at: {},
          created_at: '',
          updated_at: '',
          deleted_at: '',
          localStringId: '',
          modifiedFromLocal: false,
        },
      ],
      quality_inspections: [
        {
          id: 0,
          task_id: 0,
          company_id: 0,
          zone_id: 0,
          created_user_id: 0,
          reviewed_user_id: 0,
          round: 0,
          name: '',
          building: '',
          unit: '',
          section: '',
          inspector_name: '',
          inspected_date: '',
          status: '',
          memo: '',
          sort_key: '',
          fields_updated_at: {},
          created_at: '',
          updated_at: '',
          deleted_at: '',
          localStringId: '',
          modifiedFromLocal: false,
        },
      ],
      quality_inspection_results: [
        {
          id: 0,
          inspection_id: 0,
          item_id: 0,
          task_id: 0,
          company_id: 0,
          created_user_id: 0,
          location: '',
          location_detail: {},
          result: '',
          note: '',
          floorplan_id: 0,
          shape_type: '',
          shape_data: {},
          balloon_number: 0,
          sort_key: '',
          fields_updated_at: {},
          created_at: '',
          updated_at: '',
          deleted_at: '',
          localStringId: '',
          modifiedFromLocal: false,
        },
      ],
      quality_result_photos: [
        {
          id: 0,
          result_id: 0,
          inspection_id: 0,
          task_id: 0,
          company_id: 0,
          created_user_id: 0,
          url: '',
          photo_type: '',
          caption: '',
          sort_key: '',
          fields_updated_at: {},
          created_at: '',
          updated_at: '',
          deleted_at: '',
          localResultId: '',
          localStringId: '',
          modifiedFromLocal: false,
        },
      ],
      quality_partners: [
        {
          id: 0,
          task_id: 0,
          company_id: 0,
          created_user_id: 0,
          section: '',
          trade: '',
          partner_name: '',
          manager_name: '',
          sort_key: '',
          fields_updated_at: {},
          created_at: '',
          updated_at: '',
          deleted_at: '',
          localStringId: '',
          modifiedFromLocal: false,
        },
      ],
    }
  }

  private async optimizeDatabase(): Promise<void> {
    if (!this.db) return

    // ⚠️ 모든 PRAGMA는 트랜잭션 밖에서 실행할 것.
    // execute 기본값(transaction=true)은 문장을 BEGIN/COMMIT으로 감싸는데,
    // journal_mode/foreign_keys 등은 트랜잭션 안에서 변경이 조용히 무시된다.
    // 값을 반환하는 PRAGMA는 execute(execSQL)가 예외를 던지므로 query로 실행하고,
    // 각 문장을 개별 방어해 하나가 실패해도 나머지 설정이 적용되게 한다.
    const pragmas: Array<{ sql: string; useQuery: boolean }> = [
      { sql: 'PRAGMA foreign_keys=ON', useQuery: false },
      { sql: 'PRAGMA journal_mode=WAL', useQuery: true },
      // WAL과 조합되는 표준 설정: 커밋마다 fsync 대신 checkpoint 시점에만 동기화
      { sql: 'PRAGMA synchronous=NORMAL', useQuery: false },
      { sql: 'PRAGMA cache_size=10000', useQuery: false },
      { sql: 'PRAGMA temp_store=MEMORY', useQuery: false },
      // auto-checkpoint 비활성화: 임계를 넘는 순간에 커밋한 트랜잭션(주로 사용자
      // 조작의 단건 쓰기)이 WAL 전체 checkpoint 비용을 떠안는 것을 방지.
      // checkpoint는 SyncManager 사이클 종료 시 PASSIVE로 백그라운드 수행한다.
      { sql: 'PRAGMA wal_autocheckpoint=0', useQuery: true },
      { sql: 'PRAGMA journal_size_limit=16777216', useQuery: true },
      // 기존 WAL 즉시 checkpoint + truncate
      { sql: 'PRAGMA wal_checkpoint(TRUNCATE)', useQuery: true },
    ]
    for (const pragma of pragmas) {
      try {
        if (pragma.useQuery) {
          await this.db.query(pragma.sql)
        } else {
          await this.db.execute(pragma.sql, false)
        }
      } catch (error) {
        console.warn(`[SQLITE] PRAGMA 적용 실패 (${pragma.sql}):`, error)
      }
    }

    // 적용 검증 로그 - WAL/synchronous가 실제로 걸렸는지 실기기에서 확인용
    try {
      const journalMode = await this.db.query('PRAGMA journal_mode')
      const synchronous = await this.db.query('PRAGMA synchronous')
      console.info(
        `[SQLITE] journal_mode=${JSON.stringify(journalMode.values?.[0] ?? null)} synchronous=${JSON.stringify(synchronous.values?.[0] ?? null)}`,
      )
    } catch (error) {
      console.warn('[SQLITE] PRAGMA 검증 실패:', error)
    }
  }

  private async loadExistingTables(): Promise<void> {
    if (!this.db) return

    try {
      const result = await this.db.query('SELECT name FROM sqlite_master WHERE type="table"')
      this.createdTables.clear()

      if (result.values) {
        const systemTables = ['sqlite_sequence', 'sqlite_master']
        for (const table of result.values) {
          const tableName = table.name
          if (!systemTables.includes(tableName) && !tableName.startsWith('sqlite_')) {
            this.createdTables.add(tableName)
          }
        }
      }
      console.info(`Loaded ${this.createdTables.size} existing tables`)
    } catch (error) {
      console.error('Error loading existing tables:', error)
    }
  }

  public async closeConnection(): Promise<void> {
    if (this.db) {
      try {
        await this.sqliteConnection.closeConnection(this.dbName, false)
        console.info('Database connection closed successfully')
      } catch (error) {
        console.error('Error closing database connection:', error)
      } finally {
        this.db = null
        this.createdTables.clear()
        this.isConnecting = false
      }
    }
  }

  public async forceReset(): Promise<void> {
    console.info('Force resetting SQLite connection...')

    // 모든 상태 초기화
    this.db = null
    this.createdTables.clear()
    this.isConnecting = false

    try {
      // 모든 기존 연결 강제 정리
      const existingConnections = await this.sqliteConnection.retrieveAllConnections()
      const closePromises: Promise<void>[] = []

      for (const [dbName] of existingConnections.entries()) {
        closePromises.push(
          this.sqliteConnection
            .closeConnection(dbName, false)
            .then(() => console.info(`Closed existing connection: ${dbName}`))
            .catch(error => console.warn(`Failed to close connection ${dbName}:`, error)),
        )
      }

      // 모든 연결 종료 대기
      await Promise.allSettled(closePromises)

      // 추가 정리 시간
      await new Promise(resolve => setTimeout(resolve, 500))
    } catch (error) {
      console.warn('Error during connection cleanup:', error)
    }

    // 새 연결 생성 시도
    try {
      await this.getDatabase()
      console.info('SQLite force reset completed successfully')
    } catch (error) {
      console.error('Failed to create new connection after reset:', error)

      // 재시도 로직
      await new Promise(resolve => setTimeout(resolve, 1000))
      try {
        await this.getDatabase()
        console.info('SQLite connection established after retry')
      } catch (retryError) {
        console.error('Failed to establish connection after retry:', retryError)
        throw retryError
      }
    }
  }
}

// 앱 시작 시 DB 초기화 함수
export async function initializeSQLiteDatabase(): Promise<void> {
  try {
    console.info('Starting SQLite database initialization...')

    // 코드푸시 리로드 후인지 확인
    const isCodepushReload = localStorage.getItem('codepush_reload_flag') === 'true'
    if (isCodepushReload) {
      console.info('Detected codepush reload, performing force reset...')
      localStorage.removeItem('codepush_reload_flag')

      // 잠시 대기
      await new Promise(resolve => setTimeout(resolve, 500))
    }

    await SQLiteManager.getInstance().forceReset()
    console.info('SQLite database initialized successfully')
  } catch (error) {
    console.error('Failed to initialize SQLite database:', error)
  }
}

export const SQL = SQLiteManager.getInstance()
